import {
  IGeoJsonGeometryDto,
  ILocationNodeSimpleResponse,
  ILocationTreeNodeResponse,
  INodeGeometryResponse,
} from 'types/api/locationRegistryApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { ILocationGeos, ILocationGeo, ILocationAttrsInput, ILocationNode } from 'types/locationsRegistry.interface'

const SITE_LOCATION_TYPE = 1

export const LOCATION_REGISTRY_LINK = 'locations'

export const parseLocationRegistryOptions = (
  data: IPaginatedResponse<ILocationNodeSimpleResponse> | ILocationNodeSimpleResponse[],
) => {
  const items = Array.isArray(data) ? data : data.data ?? []
  return items.map((item) => ({ label: item.name, value: item.id }))
}

export const parseLocationRegistryPickItems = (
  data: IPaginatedResponse<ILocationNodeSimpleResponse> | ILocationNodeSimpleResponse[],
) => parseLocationRegistryOptions(data).map((item) => ({ id: item.value, name: item.label }))

export const LOCATION_ATTR_KEYS = {
  material: 'Material',
  capacityPeople: 'Capacity (people)',
  typicalSpeed: 'Typical speed (km/h)',
  surface: 'Surface',
  beds: 'Beds',
  capacityLitres: 'Capacity (L)',
  kind: 'Kind',
  statutoryInspection: 'Statutory daily inspection',
}

export const LOCATION_TYPE_FROM_API: { [key: number]: string } = {
  1: 'SITE',
  2: 'AREA',
  3: 'ZONE',
  4: 'SECTION',
  5: 'BLOCK',
  6: 'PIT',
  7: 'STOCKPILE',
  8: 'DUMP',
  9: 'PLANT_SECTION',
  10: 'BUILDING',
  11: 'BAY',
  12: 'FUEL_STORAGE',
  13: 'CAMP_BLOCK',
  14: 'ROOM',
  15: 'TSF',
  16: 'MUSTER',
  17: 'ROAD',
}

export const LOCATION_TYPE_TO_API: { [key: string]: number } = {
  SITE: 1,
  AREA: 2,
  ZONE: 3,
  SECTION: 4,
  BLOCK: 5,
  PIT: 6,
  STOCKPILE: 7,
  DUMP: 8,
  PLANT_SECTION: 9,
  BUILDING: 10,
  BAY: 11,
  FUEL_STORAGE: 12,
  CAMP_BLOCK: 13,
  ROOM: 14,
  TSF: 15,
  MUSTER: 16,
  ROAD: 17,
}

export const STATUS_FROM_API: { [key: number]: string } = {
  1: 'OPERATIONAL',
  2: 'HELD',
  3: 'EVACUATED',
}

export const STATUS_TO_API: { [key: string]: number } = {
  OPERATIONAL: 1,
  HELD: 2,
  EVACUATED: 3,
}

export const LIFECYCLE_FROM_API: { [key: number]: string } = {
  1: 'PLANNED',
  2: 'ACTIVE',
  3: 'DEPLETED',
  4: 'REHABILITATED',
}

export const HOLD_CATEGORY_FROM_API: { [key: number]: string } = {
  1: 'GROUND',
  2: 'WEATHER',
  3: 'EQUIPMENT',
  4: 'SAFETY',
  5: 'OTHER',
}

export const HOLD_CATEGORY_TO_API: { [key: string]: number } = {
  GROUND: 1,
  WEATHER: 2,
  EQUIPMENT: 3,
  SAFETY: 4,
  OTHER: 5,
}

const lngLatToPt = (coord: number[]): number[] => [coord[1], coord[0]]

export const geoJsonToPts = (geojson: IGeoJsonGeometryDto): number[][] => {
  if (geojson.type === 'Point') {
    return [lngLatToPt(geojson.coordinates as number[])]
  }
  if (geojson.type === 'LineString') {
    return (geojson.coordinates as number[][]).map(lngLatToPt)
  }
  const ring = (geojson.coordinates as number[][][])[0]
  const pts = ring.map(lngLatToPt)
  if (pts.length > 1) {
    const first = pts[0]
    const last = pts[pts.length - 1]
    if (first[0] === last[0] && first[1] === last[1]) {
      return pts.slice(0, -1)
    }
  }
  return pts
}

export const ptsToGeoJson = (draft: number[][], nodeType: string): IGeoJsonGeometryDto => {
  if (nodeType === 'ROAD') {
    return {
      type: 'LineString',
      coordinates: draft.map(([lat, lng]) => [lng, lat]),
    }
  }
  if (draft.length === 1) {
    const [lat, lng] = draft[0]
    return { type: 'Point', coordinates: [lng, lat] }
  }
  const ring = draft.map(([lat, lng]) => [lng, lat])
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([...first])
  }
  return { type: 'Polygon', coordinates: [ring] }
}

const mapGeometryToGeo = (
  geometry: INodeGeometryResponse,
  attrs: { [key: string]: unknown } | null,
  restricted: boolean,
): ILocationGeo => {
  const kind =
    geometry.kind === 1 ? 'point' : geometry.kind === 2 ? 'polygon' : ('polyline' as const)
  const speedRaw =
    attrs?.[LOCATION_ATTR_KEYS.typicalSpeed] ?? attrs?.Speed ?? attrs?.speed
  const speed = speedRaw != null ? Number(speedRaw) : undefined
  return {
    kind,
    pts: geoJsonToPts(geometry.geojson),
    restricted: restricted || undefined,
    speed: kind === 'polyline' && speed ? speed : undefined,
  }
}

export const mapApiNodeToUi = (
  node: ILocationTreeNodeResponse,
  geos: ILocationGeos,
): ILocationNode => {
  if (node.geometry) {
    geos[node.id] = mapGeometryToGeo(node.geometry, node.attrs, node.restricted)
  }

  const lifecycle = node.lifecycle ? LIFECYCLE_FROM_API[node.lifecycle] : undefined
  const status = lifecycle || STATUS_FROM_API[node.status] || 'OPERATIONAL'

  return {
    id: node.id,
    type: LOCATION_TYPE_FROM_API[node.type] || 'AREA',
    name: node.name,
    status,
    lifecycle,
    attrs: (node.attrs as ILocationNode['attrs']) || undefined,
    restricted: node.restricted,
    isUnmapped: node.isUnmapped,
    holdReason: node.holdReason || undefined,
    holdCategory: node.holdCategory ? HOLD_CATEGORY_FROM_API[node.holdCategory] : undefined,
    mounted: node.mountedRef ? `${node.mountedRef.table} #${node.mountedRef.id}` : undefined,
    children: (node.children || []).map((child) => mapApiNodeToUi(child, geos)),
  }
}

export const mapTreeResponse = (
  nodes: ILocationTreeNodeResponse[],
): { tree: ILocationNode; geos: ILocationGeos } => {
  const geos: ILocationGeos = {}

  if (nodes.length === 1) {
    return { tree: mapApiNodeToUi(nodes[0], geos), geos }
  }

  const children = nodes.map((node) => mapApiNodeToUi(node, geos))
  return {
    tree: {
      id: 0,
      type: 'SITE',
      name: 'Sites',
      status: 'OPERATIONAL',
      restricted: false,
      isUnmapped: false,
      children,
    },
    geos,
  }
}

export const mapFlatNodes = (nodes: ILocationTreeNodeResponse[]): ILocationNode[] => {
  const geos: ILocationGeos = {}
  return nodes.map((node) => mapApiNodeToUi(node, geos))
}

export const findFirstAreaGeoId = (tree: ILocationNode, geos: ILocationGeos): number | null => {
  const walk = (node: ILocationNode): number | null => {
    if (node.type === 'AREA' && geos[node.id]) return node.id
    for (const child of node.children || []) {
      const found = walk(child)
      if (found) return found
    }
    return null
  }
  return walk(tree)
}

export const findRoadsAreaId = (tree: ILocationNode): number | null => {
  const walk = (node: ILocationNode): number | null => {
    if (node.type === 'AREA' && node.children?.some((child) => child.type === 'ROAD')) {
      return node.id
    }
    for (const child of node.children || []) {
      const found = walk(child)
      if (found) return found
    }
    return null
  }
  return walk(tree)
}

export const buildCreateLocationAttrs = (
  type: string,
  form: ILocationAttrsInput,
): { [key: string]: string | number | boolean } | null => {
  const attrs: { [key: string]: string | number | boolean } = {}

  if (['STOCKPILE', 'DUMP'].includes(type) && form.material) {
    attrs[LOCATION_ATTR_KEYS.material] = form.material
  }
  if (type === 'MUSTER' && form.capacity) {
    attrs[LOCATION_ATTR_KEYS.capacityPeople] = Number(form.capacity)
  }
  if (type === 'ROAD') {
    if (form.speed && Number(form.speed) > 0) {
      attrs[LOCATION_ATTR_KEYS.typicalSpeed] = Number(form.speed)
    }
    if (form.surface) attrs[LOCATION_ATTR_KEYS.surface] = form.surface
  }
  if (type === 'ROOM' && form.beds) {
    attrs[LOCATION_ATTR_KEYS.beds] = Number(form.beds)
  }
  if (type === 'FUEL_STORAGE') {
    if (form.fuelCap) attrs[LOCATION_ATTR_KEYS.capacityLitres] = Number(form.fuelCap)
    if (form.fuelKind) attrs[LOCATION_ATTR_KEYS.kind] = form.fuelKind
  }
  if (type === 'TSF' && form.statutory) {
    attrs[LOCATION_ATTR_KEYS.statutoryInspection] = true
  }

  return Object.keys(attrs).length > 0 ? attrs : null
}

export const isStatutoryLocation = (attrs?: ILocationNode['attrs']): boolean =>
  Boolean(attrs?.statutory || attrs?.[LOCATION_ATTR_KEYS.statutoryInspection])
